import { ChevronDownIcon, FileTextIcon, FolderIcon } from 'lucide-react';
import { useState } from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Text } from '@/components/ui/catalyst/text';
import { cn } from '@/lib/utils';
import { useListAgentChatWorkspaceExhibitsQuery } from '@/services/api/agentChatWorkspaceService';
import { AgentChatWorkspace } from '@/services/types/agent-chat-workspace-types';

import { EditWorkspaceDialog } from './EditWorkspaceDialog';
import { WorkspaceListItem } from './WorkspaceListItem';

interface WorkspaceListProps {
  workspaces: AgentChatWorkspace[];
  isLoading: boolean;
  selectedWorkspaceId: string | null;
  matterId: string;
  onWorkspaceSelect: (workspaceId: string) => void;
  onCreateWorkspace: () => void;
  onDeleteWorkspace: (workspaceId: string) => void;
  onChangeTab: (tab: TabType) => void;
  activeTab: TabType;
}

export type TabType = 'document' | 'exhibits';
const WorkspaceDocument = () => {
  return (
    <>
      <FileTextIcon className="h-4 w-4 mr-3" />
      Document
    </>
  );
};
const WorkspaceExhibits = ({
  workspaceId,
  matterId,
}: {
  workspaceId: string;
  matterId: string;
}) => {
  const { data: exhibits = [], isLoading: isExhibitsLoading } =
    useListAgentChatWorkspaceExhibitsQuery({
      matterId,
      workspaceId,
    });

  return (
    <>
      <FolderIcon className="h-4 w-4 mr-3" />
      Exhibits
      <span className="ml-auto bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full">
        {isExhibitsLoading ? (
          <div role="status">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          exhibits.length
        )}
      </span>
    </>
  );
};

export const WorkspaceSidebar = ({
  workspaces,
  isLoading,
  selectedWorkspaceId,
  matterId,
  onWorkspaceSelect,
  onCreateWorkspace,
  onDeleteWorkspace,
  onChangeTab,
  activeTab,
}: WorkspaceListProps) => {
  const [workspaceToEdit, setWorkspaceToEdit] =
    useState<AgentChatWorkspace | null>(null);

  const selectedWorkspace = workspaces.find(
    (w) => w.id === selectedWorkspaceId,
  );

  const handleRenameWorkspace = (workspace: AgentChatWorkspace) => {
    setWorkspaceToEdit(workspace);
  };

  const handleDeleteWorkspace = (workspaceId: string) => {
    onDeleteWorkspace(workspaceId);
  };

  const handleEditDialogClose = () => {
    setWorkspaceToEdit(null);
  };

  const handleTabChange = (tab: TabType) => {
    onChangeTab(tab);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded-md mb-4" />
          <div className="h-8 bg-gray-200 rounded-md mb-2" />
          <div className="h-8 bg-gray-200 rounded-md mb-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Workspace Dropdown */}
      <div className="p-4 border-b">
        <Dropdown>
          <DropdownButton
            outline
            className="w-full! flex! justify-between! items-center! text-left"
          >
            <span className="text-sm font-medium text-gray-900 truncate">
              {selectedWorkspace?.name || 'Select Workspace'}
            </span>
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          </DropdownButton>

          <DropdownMenu>
            {workspaces.length > 0 && (
              <>
                {workspaces.map((workspace) => (
                  <DropdownItem
                    key={workspace.id}
                    onClick={() => onWorkspaceSelect(workspace.id)}
                  >
                    <WorkspaceListItem
                      workspace={workspace}
                      onRename={() => handleRenameWorkspace(workspace)}
                      onDelete={() => handleDeleteWorkspace(workspace.id)}
                    />
                  </DropdownItem>
                ))}
                <DropdownDivider />
              </>
            )}
            <DropdownItem onClick={onCreateWorkspace}>
              + Create New Workspace
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>

      {/* Workspace Section */}
      {selectedWorkspace && (
        <div className="flex-1 overflow-auto">
          <div className="p-4">
            <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              WORKSPACE
            </Text>

            {/* Navigation Tabs */}
            <nav className="space-y-1">
              <button
                type="button"
                onClick={() => handleTabChange('document')}
                className={cn(
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'document'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
                )}
              >
                <WorkspaceDocument />
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('exhibits')}
                className={cn(
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  activeTab === 'exhibits'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
                )}
              >
                <WorkspaceExhibits
                  workspaceId={selectedWorkspace.id}
                  matterId={matterId}
                />
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* No workspace selected state */}
      {!selectedWorkspace && workspaces.length > 0 && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <Text className="text-gray-500 text-sm">
              Select a workspace to view its contents
            </Text>
          </div>
        </div>
      )}

      {/* Edit Workspace Dialog */}
      {workspaceToEdit && (
        <EditWorkspaceDialog
          isOpen={!!workspaceToEdit}
          onClose={handleEditDialogClose}
          matterId={matterId}
          workspace={workspaceToEdit}
        />
      )}
    </div>
  );
};
